import type { Package } from "@src/services/insurance-package.service";

export const ALL_ENROLLMENT_CATEGORIES_VALUE = "enrollment-category:all";
export const OTHER_ENROLLMENT_CATEGORY_VALUE = "enrollment-category:other";

export interface EnrollmentCategoryOption {
  value: string;
  label: string;
  categoryIds: number[];
  kind: "all" | "named" | "other";
}

const categoryName = (insurancePackage: Package): string | null => {
  const name = insurancePackage.enrollment_category?.name;
  return typeof name === "string" && name.trim() ? name : null;
};

export const getInsurancePackageDisplayName = (
  insurancePackage: Package,
  selectedCategoryValue: string | null,
): string => {
  const name = categoryName(insurancePackage);
  if (selectedCategoryValue === ALL_ENROLLMENT_CATEGORIES_VALUE && name) {
    return `${insurancePackage.name} (${name.trim()})`;
  }

  return insurancePackage.name;
};

export const deriveEnrollmentCategoryOptions = (
  insurancePackages: Package[],
): EnrollmentCategoryOption[] => {
  if (insurancePackages.length === 0) {
    return [];
  }

  const namedCategories = new Map<string, EnrollmentCategoryOption>();
  let hasMissingCategoryName = false;

  insurancePackages.forEach((insurancePackage) => {
    const enrollmentCategory = insurancePackage.enrollment_category;
    const name = categoryName(insurancePackage);
    if (!name || !enrollmentCategory) {
      hasMissingCategoryName = true;
      return;
    }

    const normalizedName = name.trim();
    const existingCategory = namedCategories.get(normalizedName);
    if (existingCategory) {
      if (!existingCategory.categoryIds.includes(enrollmentCategory.id)) {
        existingCategory.categoryIds.push(enrollmentCategory.id);
      }
      return;
    }

    namedCategories.set(normalizedName, {
      value: `enrollment-category:named:${normalizedName}`,
      label: name,
      categoryIds: [enrollmentCategory.id],
      kind: "named",
    });
  });

  return [
    {
      value: ALL_ENROLLMENT_CATEGORIES_VALUE,
      label: "I'm not sure",
      categoryIds: [],
      kind: "all",
    },
    ...Array.from(namedCategories.values()).sort((left, right) => {
      const labelComparison = left.label.trim().localeCompare(right.label.trim(), undefined, {
        sensitivity: "base",
      });
      return labelComparison || left.label.localeCompare(right.label);
    }),
    ...(hasMissingCategoryName
      ? [
          {
            value: OTHER_ENROLLMENT_CATEGORY_VALUE,
            label: "Other",
            categoryIds: [],
            kind: "other" as const,
          },
        ]
      : []),
  ];
};

export const getEnrollmentCategoryOptionForInsurancePackage = (
  insurancePackage: Package,
  categoryOptions: EnrollmentCategoryOption[],
): EnrollmentCategoryOption | undefined => {
  const enrollmentCategory = insurancePackage.enrollment_category;
  const namedCategory = categoryOptions.find(
    (categoryOption) =>
      categoryOption.kind === "named" &&
      categoryOption.categoryIds.includes(enrollmentCategory?.id ?? -1),
  );

  if (namedCategory) {
    return namedCategory;
  }

  if (!enrollmentCategory || categoryName(insurancePackage) === null) {
    return categoryOptions.find((categoryOption) => categoryOption.kind === "other");
  }

  return undefined;
};

export const filterInsurancePackagesByEnrollmentCategory = (
  insurancePackages: Package[],
  selectedCategoryValue: string | null,
  categoryOptions: EnrollmentCategoryOption[],
): Package[] => {
  const selectedCategory = categoryOptions.find(
    (categoryOption) => categoryOption.value === selectedCategoryValue,
  );

  if (categoryOptions.length === 0) {
    return insurancePackages;
  }

  if (!selectedCategory) {
    return [];
  }

  if (selectedCategory.kind === "all") {
    return insurancePackages;
  }

  if (selectedCategory.kind === "other") {
    return insurancePackages.filter((insurancePackage) => categoryName(insurancePackage) === null);
  }

  return insurancePackages.filter((insurancePackage) => {
    return selectedCategory.categoryIds.includes(insurancePackage.enrollment_category?.id ?? -1);
  });
};
